import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type VoiceCallState = 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'in_call';

@Component({
  selector: 'app-voice-call-overlay',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container>

      <!-- ════════════════════════════════════════════
           INCOMING CALL / CONNECTING  — full-screen blurred overlay
           ════════════════════════════════════════════ -->
      <div
        *ngIf="state === 'incoming' || state === 'connecting'"
        class="fixed inset-0 z-[9998] flex items-center justify-center"
        style="background: rgba(18,24,38,0.72); backdrop-filter: blur(8px);"
      >
        <!-- Animated ring circles -->
        <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div class="ring-pulse ring-1"></div>
          <div class="ring-pulse ring-2"></div>
          <div class="ring-pulse ring-3"></div>
        </div>

        <div
          class="relative z-10 flex flex-col items-center gap-6 px-8 py-10 rounded-3xl shadow-2xl"
          style="background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.14); max-width: 340px; width: 100%;"
        >
          <!-- Label -->
          <span class="text-xs font-medium tracking-widest uppercase text-navy-hero">
            {{ state === 'connecting' ? 'Connecting...' : 'Incoming Voice Call' }}
          </span>

          <!-- Avatar -->
          <div class="relative">
            <img
              [src]="callerPhotoUrl || '/assets/images/placeholder-company.png'"
              alt="Caller"
              class="w-24 h-24 rounded-full object-cover shadow-lg"
              style="border: 3px solid rgba(107,123,163,0.5);"
            />
            <span
              class="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
              style="background: #17b26a;"
            >
              <svg width="10" height="10" viewBox="0 0 20 20" fill="white">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
              </svg>
            </span>
          </div>

          <!-- Caller name -->
          <div class="text-center">
            <h3 class="text-xl font-semibold text-white">{{ callerName || 'Unknown' }}</h3>
            <p class="text-sm mt-1" style="color: rgba(255,255,255,0.5);">is calling you…</p>
          </div>

          <!-- Action buttons -->
          <div class="flex items-center gap-10 mt-2">
            <!-- Decline -->
            <div class="flex flex-col items-center gap-2">
              <button
                (click)="onDecline.emit()"
                [disabled]="state === 'connecting'"
                [class.opacity-50]="state === 'connecting'"
                [style.cursor]="state === 'connecting' ? 'not-allowed' : 'pointer'"
                class="w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-transform active:scale-95 hover:brightness-110"
                style="background: #ef2c2d;"
                title="Decline"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                  <path d="M13.5 4.07c.42-.1.85-.07 1.28.07L17 5l.35.35a1 1 0 010 1.41l-2 2a1 1 0 01-1.32.08l-1.1-.83a10.63 10.63 0 00-2.86 2.86l.83 1.1a1 1 0 01-.08 1.32l-2 2A1 1 0 017.4 15L7 14.65l-.93-2.22a2 2 0 01.06-1.68A15 15 0 0113.5 4.07zM6.29 6.29a1 1 0 011.42 0l10 10a1 1 0 01-1.42 1.42l-10-10a1 1 0 010-1.42z"/>
                </svg>
              </button>
              <span class="text-xs" style="color: rgba(255,255,255,0.6);">Decline</span>
            </div>

            <!-- Accept -->
            <div class="flex flex-col items-center gap-2">
              <button
                (click)="onAccept.emit()"
                [disabled]="state === 'connecting'"
                [class.opacity-50]="state === 'connecting'"
                [style.cursor]="state === 'connecting' ? 'not-allowed' : 'pointer'"
                class="w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-transform active:scale-95 hover:brightness-110"
                style="background: #17b26a;"
                title="Accept"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                </svg>
              </button>
              <span class="text-xs" style="color: rgba(255,255,255,0.6);">Accept</span>
            </div>
          </div>
        </div>
      </div>

      <ng-container [ngSwitch]="state">

      <!-- ════════════════════════════════════════════
           OUTGOING CALL — full-screen blurred overlay
           ════════════════════════════════════════════ -->
      <div
        *ngSwitchCase="'outgoing'"
        class="fixed inset-0 z-[9998] flex items-center justify-center"
        style="background: rgba(18,24,38,0.72); backdrop-filter: blur(8px);"
      >
        <div
          class="relative z-10 flex flex-col items-center gap-6 px-8 py-10 rounded-3xl shadow-2xl"
          style="background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.14); max-width: 340px; width: 100%;"
        >
          <span class="text-xs font-medium tracking-widest uppercase text-navy-hero">
            Voice Call
          </span>

          <!-- Pulsing avatar -->
          <div class="relative flex items-center justify-center">
            <div class="absolute w-32 h-32 rounded-full animate-ping opacity-20" style="background: var(--cp-navy-hero);"></div>
            <div class="absolute w-28 h-28 rounded-full animate-pulse opacity-10" style="background: var(--cp-navy-hero);"></div>
            <img
              [src]="callerPhotoUrl || '/assets/images/placeholder-company.png'"
              alt="Callee"
              class="relative w-24 h-24 rounded-full object-cover shadow-lg z-10"
              style="border: 3px solid rgba(107,123,163,0.5);"
            />
          </div>

          <div class="text-center">
            <h3 class="text-xl font-semibold text-white">{{ callerName || 'Unknown' }}</h3>
            <div class="flex items-center justify-center gap-1 mt-2">
              <div class="w-1.5 h-1.5 rounded-full bg-navy-hero animate-bounce" style="animation-delay: 0ms;"></div>
              <div class="w-1.5 h-1.5 rounded-full bg-navy-hero animate-bounce" style="animation-delay: 150ms;"></div>
              <div class="w-1.5 h-1.5 rounded-full bg-navy-hero animate-bounce" style="animation-delay: 300ms;"></div>
              <span class="text-sm ml-1" style="color: rgba(255,255,255,0.5);">Calling</span>
            </div>
          </div>

          <!-- Cancel -->
          <button
            (click)="onCancel.emit()"
            class="mt-2 w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-transform active:scale-95 hover:brightness-110"
            style="background: #ef2c2d;"
            title="Cancel Call"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
              <path d="M6.29 6.29a1 1 0 011.42 0l10 10a1 1 0 01-1.42 1.42l-10-10a1 1 0 010-1.42z"/>
              <path d="M13.5 4.07c.42-.1.85-.07 1.28.07L17 5l.35.35a1 1 0 010 1.41l-2 2a1 1 0 01-1.32.08l-1.1-.83a10.63 10.63 0 00-2.86 2.86l.83 1.1a1 1 0 01-.08 1.32l-2 2A1 1 0 017.4 15L7 14.65l-.93-2.22a2 2 0 01.06-1.68A15 15 0 0113.5 4.07z"/>
            </svg>
          </button>
          <span class="text-xs -mt-4" style="color: rgba(255,255,255,0.6);">Cancel</span>
        </div>
      </div>

      <!-- ════════════════════════════════════════════
           IN CALL  — slim top banner (non-blocking)
           Sits just below the browser chrome; leaves the full
           chat message area and composer completely accessible.
           ════════════════════════════════════════════ -->
      <div
        *ngSwitchCase="'in_call'"
        class="fixed top-3 left-1/2 z-[9997] flex w-[min(560px,calc(100vw-6rem))] -translate-x-1/2 items-center gap-3 rounded-2xl px-4 py-2 shadow-xl"
        style="background: rgba(18,24,38,0.92); border: 1px solid rgba(107,123,163,0.3); backdrop-filter: blur(16px);"
      >
        <!-- Avatar + name + timer -->
        <div class="flex items-center gap-2.5 flex-1 min-w-0">
          <img
            [src]="callerPhotoUrl || '/assets/images/placeholder-company.png'"
            alt="Caller"
            class="w-7 h-7 rounded-full object-cover flex-shrink-0"
            style="border: 2px solid rgba(107,123,163,0.4);"
          />
          <div class="min-w-0 flex items-center gap-2">
            <p class="text-white text-sm font-medium truncate">{{ callerName || 'Unknown' }}</p>
            <span class="text-[#17b26a] text-xs font-mono flex-shrink-0">{{ formattedDuration }}</span>
          </div>
        </div>

        <!-- Controls -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <!-- Mute -->
          <button
            (click)="toggleMute()"
            class="w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-95"
            [style.background]="isMuted ? 'rgba(239,44,45,0.25)' : 'rgba(107,123,163,0.2)'"
            [title]="isMuted ? 'Unmute' : 'Mute'"
          >
            <svg *ngIf="!isMuted" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
            <svg *ngIf="isMuted" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef2c2d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="1" y1="1" x2="23" y2="23"/>
              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
          </button>

          <!-- Speaker / audio-output toggle -->
          <button
            (click)="toggleSpeaker()"
            class="w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-95"
            [style.background]="isSpeakerOn ? 'rgba(107,123,163,0.4)' : 'rgba(107,123,163,0.15)'"
            [title]="speakerButtonTitle"
          >
            <!-- Speaker ON (loud) -->
            <svg *ngIf="isSpeakerOn" width="15" height="15" viewBox="0 0 24 24" fill="none"
              stroke="#6B7BA3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
            </svg>
            <!-- Headphones (when headphones detected) — same as speaker-off visual but with headphone icon -->
            <svg *ngIf="!isSpeakerOn" width="15" height="15" viewBox="0 0 24 24" fill="none"
              stroke="rgba(255,255,255,0.55)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              <line x1="23" y1="9" x2="17" y2="15"/>
              <line x1="17" y1="9" x2="23" y2="15"/>
            </svg>
          </button>

          <!-- End Call -->
          <button
            (click)="onEndCall.emit()"
            class="w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-transform active:scale-95 hover:brightness-110"
            style="background: #ef2c2d;"
            title="End Call"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="white">
              <path d="M6.29 6.29a1 1 0 011.42 0l10 10a1 1 0 01-1.42 1.42l-10-10a1 1 0 010-1.42z"/>
              <path d="M13.5 4.07c.42-.1.85-.07 1.28.07L17 5l.35.35a1 1 0 010 1.41l-2 2a1 1 0 01-1.32.08l-1.1-.83a10.63 10.63 0 00-2.86 2.86l.83 1.1a1 1 0 01-.08 1.32l-2 2A1 1 0 017.4 15L7 14.65l-.93-2.22a2 2 0 01.06-1.68A15 15 0 0113.5 4.07z"/>
            </svg>
          </button>
        </div>
      </div>

      </ng-container>
    </ng-container>
  `,
  styles: [`
    /* Pulsing ring circles for incoming call */
    .ring-pulse {
      position: absolute;
      border-radius: 50%;
      border: 2px solid rgba(107, 123, 163, 0.3);
      animation: ringExpand 2.4s ease-out infinite;
    }
    .ring-1 { width: 220px; height: 220px; animation-delay: 0s; }
    .ring-2 { width: 280px; height: 280px; animation-delay: 0.6s; }
    .ring-3 { width: 340px; height: 340px; animation-delay: 1.2s; }

    @keyframes ringExpand {
      0%   { transform: scale(0.85); opacity: 0.5; }
      80%  { transform: scale(1.1);  opacity: 0; }
      100% { opacity: 0; }
    }
  `],
})
export class VoiceCallOverlayComponent implements OnInit, OnChanges, OnDestroy {
  @Input() state: VoiceCallState = 'idle';
  @Input() callerName: string = '';
  @Input() callerPhotoUrl: string = '';
  /** Controlled by parent (via VoiceCallService) */
  @Input() isMuted: boolean = false;
  @Input() isSpeakerOn: boolean = false;

  @Output() onAccept       = new EventEmitter<void>();
  @Output() onDecline      = new EventEmitter<void>();
  @Output() onCancel       = new EventEmitter<void>();
  @Output() onEndCall      = new EventEmitter<void>();
  /** Emits the desired new mute state (true = muted) */
  @Output() onMuteToggle   = new EventEmitter<boolean>();
  /** Emits the desired new speaker state (true = on) */
  @Output() onSpeakerToggle = new EventEmitter<boolean>();

  private callSeconds = 0;
  private timerInterval: ReturnType<typeof setInterval> | null = null;

  get formattedDuration(): string {
    const m = Math.floor(this.callSeconds / 60).toString().padStart(2, '0');
    const s = (this.callSeconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  /** Tooltip reflects the audio route that will become active after tapping. */
  get speakerButtonTitle(): string {
    return this.isSpeakerOn ? 'Switch to earpiece / headphones' : 'Switch to loudspeaker';
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.state === 'in_call') this.startTimer();
  }

  ngOnChanges(): void {
    if (this.state === 'in_call' && !this.timerInterval) {
      this.startTimer();
    } else if (this.state !== 'in_call') {
      this.stopTimer();
      this.callSeconds = 0;
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  toggleMute(): void {
    this.onMuteToggle.emit(!this.isMuted);
  }

  toggleSpeaker(): void {
    this.onSpeakerToggle.emit(!this.isSpeakerOn);
  }

  private startTimer(): void {
    this.stopTimer();
    this.callSeconds = 0;
    this.timerInterval = setInterval(() => {
      this.callSeconds++;
      this.cdr.markForCheck();
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
}
